import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'
import Snackbar from '@material-ui/core/Snackbar'
import IconButton from '@material-ui/core/IconButton'
import CircularProgress from '@material-ui/core/CircularProgress'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName } from '@fortawesome/fontawesome-svg-core'

export type SnackbarType = 'notice' | 'progress' | 'success' | 'error' | 'warn' | 'hint'

interface EnqueueParams {
  type: SnackbarType
  message: string
  autoHideDuration?: number | null
}

interface SnackbarContextValue {
  enqueue: (params: EnqueueParams) => number
  closeQueue: (id: number) => void
}

interface SnackbarItem {
  id: number
  type: SnackbarType
  message: string
  autoHideDuration: number | null
}

const MAX_STACK = 3
const DEFAULT_AUTO_HIDE = 4000
const ITEM_OFFSET = 56
const TOP_BASE = 72
const Z_INDEX = 10020

const TYPE_STYLE: Record<SnackbarType, { accent: string; icon: IconName | null }> = {
  notice: { accent: '#2f80ed', icon: 'info-circle' },
  progress: { accent: '#4f4f4f', icon: null },
  success: { accent: '#27ae60', icon: 'check-circle' },
  error: { accent: '#eb5757', icon: 'exclamation-circle' },
  warn: { accent: '#f2994a', icon: 'exclamation-triangle' },
  hint: { accent: '#f2c94c', icon: 'lightbulb' },
}

const SnackbarContext = createContext<SnackbarContextValue | null>(null)

export const useSnackbar = (): SnackbarContextValue => {
  const ctx = useContext(SnackbarContext)
  if (!ctx) {
    throw new Error('useSnackbar は SnackbarProvider の内側で使用してください')
  }
  return ctx
}

export const SnackbarProvider: FC = ({ children }) => {
  const [items, setItems] = useState<SnackbarItem[]>([])
  const nextId = useRef(1)

  const closeQueue = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const enqueue = useCallback((params: EnqueueParams): number => {
    const id = nextId.current
    nextId.current += 1
    let autoHideDuration: number | null = DEFAULT_AUTO_HIDE
    if (params.autoHideDuration !== undefined) {
      autoHideDuration = params.autoHideDuration
    } else if (params.type === 'progress') {
      autoHideDuration = null
    }
    const item: SnackbarItem = {
      id,
      type: params.type,
      message: params.message,
      autoHideDuration,
    }
    // 新しいものを先頭(=一番上)に積み、最大3件まで保持
    setItems((prev) => [item, ...prev].slice(0, MAX_STACK))
    return id
  }, [])

  return (
    <SnackbarContext.Provider value={{ enqueue, closeQueue }}>
      {children}
      {items.map((item, index) => {
        const style = TYPE_STYLE[item.type]
        return (
          <Snackbar
            key={item.id}
            open
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            autoHideDuration={item.autoHideDuration}
            onClose={(_event, reason) => {
              if (reason === 'clickaway') return
              closeQueue(item.id)
            }}
            style={{ top: TOP_BASE + index * ITEM_OFFSET, zIndex: Z_INDEX }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 288,
                maxWidth: 480,
                padding: '6px 8px 6px 16px',
                borderRadius: 4,
                color: '#333',
                background: '#fff',
                borderLeft: `4px solid ${style.accent}`,
                boxShadow:
                  '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  marginRight: 12,
                  fontSize: 16,
                  color: style.accent,
                }}
              >
                {item.type === 'progress' ? (
                  <CircularProgress size={18} thickness={5} style={{ color: style.accent }} />
                ) : (
                  style.icon && <FontAwesomeIcon icon={['fas', style.icon]} />
                )}
              </span>
              <span style={{ flex: 1, fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-line' }}>
                {item.message}
              </span>
              <IconButton
                size="small"
                aria-label="close"
                onClick={() => closeQueue(item.id)}
                style={{ color: '#999', marginLeft: 8 }}
              >
                <FontAwesomeIcon icon={['fas', 'times']} style={{ fontSize: 16 }} />
              </IconButton>
            </div>
          </Snackbar>
        )
      })}
    </SnackbarContext.Provider>
  )
}

export default SnackbarProvider
